import { useState } from 'react';
import { Link } from 'react-router-dom';
import { upcomingEvents, pastEvents, eventCategories, archivedEvents } from '@/mocks/events';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import Seo from '@/components/feature/Seo';
import { pageSeo } from '@/mocks/seo';
import PhotoViewer from '@/pages/events/components/PhotoViewer';

const ARCHIVE_PREVIEW = 8;

export default function Events() {
  const [activePastEvent, setActivePastEvent] = useState(0);
  const [expandedEvent, setExpandedEvent] = useState<number | null>(null);
  const [archiveFilter, setArchiveFilter] = useState<number | 'all'>('all');
  const [expandedGroups, setExpandedGroups] = useState<Record<number, boolean>>({});
  const [pastLightboxIndex, setPastLightboxIndex] = useState<number | null>(null);
  const [archiveLightboxIndex, setArchiveLightboxIndex] = useState<number | null>(null);

  const heroAnim = useScrollAnimation({ threshold: 0.1 });
  const upcomingAnim = useScrollAnimation({ threshold: 0.1 });
  const categoriesAnim = useScrollAnimation({ threshold: 0.15 });
  const pastAnim = useScrollAnimation({ threshold: 0.1 });
  const archiveAnim = useScrollAnimation({ threshold: 0.1 });
  const ctaAnim = useScrollAnimation({ threshold: 0.3 });

  const toggleEvent = (id: number) => {
    setExpandedEvent(expandedEvent === id ? null : id);
  };

  const totalArchivedPhotos = archivedEvents.reduce((sum, evt) => sum + evt.photos.length, 0);

  const archiveGroups = archiveFilter === 'all'
    ? archivedEvents
    : archivedEvents.filter((evt) => evt.id === archiveFilter);

  // Each event shows a tidy preview first, then the rest on demand, so the
  // archive reads as organised albums instead of one endless wall of photos.
  let archiveCursor = 0;
  const archiveGroupsWithOffset = archiveGroups.map((evt) => {
    const visiblePhotos = expandedGroups[evt.id]
      ? evt.photos
      : evt.photos.slice(0, ARCHIVE_PREVIEW);
    const offset = archiveCursor;
    archiveCursor += visiblePhotos.length;
    return { ...evt, visiblePhotos, offset };
  });

  const visibleArchivePhotos = archiveGroupsWithOffset.flatMap((group) => group.visiblePhotos);

  return (
    <main>
      <Seo {...pageSeo.events} />
      {/* ============ HERO SECTION ============ */}
      <section className="relative w-full min-h-[500px] md:min-h-[580px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20book%20fair%20exhibition%20hall%20with%20warm%20golden%20ambient%20lighting%2C%20rows%20of%20modern%20publisher%20booths%20with%20sophisticated%20navy%20and%20cream%20branded%20displays%2C%20professional%20literary%20event%20atmosphere%2C%20soft%20chandelier%20glow%2C%20warm%20inviting%20ambiance%2C%20polished%20floors%20reflecting%20golden%20light%2C%20prestigious%20international%20publishing%20trade%20show%20setting%2C%20high-end%20exhibition%20venue%20with%20refined%20decor&width=1920&height=900&seq=events-hero-bg-2026&orientation=landscape"
            alt=""
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy-900/80 via-navy-900/65 to-navy-900/85"></div>
        </div>

        <div ref={heroAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 py-16 md:py-20 animate-on-scroll ${heroAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/20 border border-gold-400/30 rounded-full mb-6">
              <div className="w-2 h-2 rounded-full bg-gold-400 animate-pulse"></div>
              <span className="text-xs font-medium text-gold-300 uppercase tracking-widest">Mark Your Calendar</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold text-white mb-6 leading-tight">
              Upcoming <span className="text-gold-400">Events</span>
            </h1>
            <p className="text-base md:text-lg text-cream-200 leading-relaxed mb-8 max-w-3xl mx-auto">
              Join Leavitt Peak Press at prestigious book fairs and literary events around the world. Meet our authors, explore our latest titles, and discover how we bring stories to life on the global stage.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#upcoming-events"
                className="w-full sm:w-auto px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-[0_4px_20px_rgba(196,154,58,0.3)]"
              >
                View Upcoming Events
                <i className="ri-arrow-down-line ml-2"></i>
              </a>
              <a
                href="/contact-us"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Reserve Your Spot
              </a>
            </div>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent"></div>
      </section>

      {/* ============ FEATURED EVENT REPORT ============ */}
      <section className="w-full bg-white pt-12 md:pt-16 pb-2">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-0 rounded-2xl overflow-hidden border border-cream-200 bg-cream-50">
              <div className="relative min-h-[280px] lg:min-h-[420px] overflow-hidden">
                <img
                  src="https://storage.helloreaddy.io/project_files/d4a3ff26-933b-4c2f-85e6-4a8de0cf4dd2/04aa1d51-cd8a-4039-a544-58be26325257_compressed_IMG_2647.webp"
                  alt="Leavitt Peak Press booth at Frankfurt Book Fair 2026"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-navy-950/70 via-navy-950/10 to-transparent"></div>
                <div className="absolute top-5 left-5 flex flex-wrap gap-2">
                  <span className="px-3 py-1 text-[10px] font-semibold text-navy-900 bg-gold-400 rounded-full uppercase tracking-wider whitespace-nowrap">
                    Event Report
                  </span>
                  <span className="px-3 py-1 text-[10px] font-semibold text-white bg-navy-950/60 rounded-full uppercase tracking-wider whitespace-nowrap">
                    Just Concluded
                  </span>
                </div>
                <div className="absolute bottom-5 left-5 right-5 flex items-end justify-between gap-4">
                  <div className="flex items-center gap-2 text-[11px] text-white/90">
                    <i className="ri-map-pin-line text-gold-300"></i>
                    Halle 3.1 / A104, Messe Frankfurt
                  </div>
                  <div className="flex items-center gap-1.5 px-3 py-1 bg-white/15 rounded-full text-[11px] font-medium text-white whitespace-nowrap">
                    <i className="ri-camera-line"></i>
                    10 Photos
                  </div>
                </div>
              </div>

              <div className="p-6 md:p-9 flex flex-col justify-center">
                <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">
                  Latest From The Booth
                </p>
                <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-navy-900 leading-snug mb-3">
                  Frankfurt Book Fair 2026: five days, one booth, and a wall of books
                </h2>
                <p className="text-sm text-navy-600 leading-relaxed mb-5">
                  From October 7 to 11 we held down stand A104 in Halle 3.1 — two walls of title posters, a table of
                  flyers and bookmarks, and a nonstop flow of readers, scouts, and rights buyers. Here is how the
                  fair actually went.
                </p>

                <div className="grid grid-cols-3 gap-3 mb-6">
                  <div className="bg-white rounded-xl border border-cream-200 px-3 py-3 text-center">
                    <p className="text-lg font-bold text-navy-900 leading-none mb-1">18</p>
                    <p className="text-[10px] text-navy-500 uppercase tracking-wide">Genres</p>
                  </div>
                  <div className="bg-white rounded-xl border border-cream-200 px-3 py-3 text-center">
                    <p className="text-lg font-bold text-navy-900 leading-none mb-1">220</p>
                    <p className="text-[10px] text-navy-500 uppercase tracking-wide">Authors</p>
                  </div>
                  <div className="bg-white rounded-xl border border-cream-200 px-3 py-3 text-center">
                    <p className="text-lg font-bold text-navy-900 leading-none mb-1">6</p>
                    <p className="text-[10px] text-navy-500 uppercase tracking-wide">Continents</p>
                  </div>
                </div>

                <Link
                  to="/events/frankfurt-book-fair-2026"
                  className="inline-flex items-center justify-center gap-2 self-start px-6 py-3 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  Read the Full Story
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ EVENT CATEGORIES ============ */}
      <section className="w-full py-14 md:py-20 bg-white -mt-1 relative z-10">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={categoriesAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${categoriesAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-10">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">What We Attend</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Event Categories</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {eventCategories.map((cat, idx) => (
                <div
                  key={cat.title}
                  className="group relative bg-white rounded-2xl p-6 border border-cream-200 hover:border-gold-300 transition-all duration-500 hover:shadow-[0_8px_40px_rgba(196,154,58,0.12)] hover:-translate-y-2 text-center"
                  style={{ animationDelay: `${idx * 0.08}s` }}
                >
                  <div className="w-14 h-14 mx-auto mb-5 flex items-center justify-center rounded-2xl bg-gradient-to-br from-gold-100 to-gold-50 transition-all duration-500 group-hover:from-gold-500 group-hover:to-gold-400 group-hover:scale-110 group-hover:rotate-6">
                    <i className={`${cat.icon} text-xl text-gold-600 transition-all duration-500 group-hover:text-white`}></i>
                  </div>
                  <h4 className="text-base font-semibold text-navy-900 mb-2 group-hover:text-gold-600 transition-colors duration-300">
                    {cat.title}
                  </h4>
                  <p className="text-sm text-navy-600 leading-relaxed mb-4">{cat.description}</p>
                  <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-gold-50 rounded-full text-xs font-medium text-gold-600">
                    <i className="ri-calendar-event-line"></i>
                    <span>{cat.count} Events</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ UPCOMING EVENTS ============ */}
      <section id="upcoming-events" className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={upcomingAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${upcomingAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-12">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Coming Soon</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Upcoming Events in 2026</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
              <p className="text-sm text-navy-600 mt-4 max-w-2xl mx-auto">
                Join us at these major publishing events. Click on any event to learn more and reserve your spot at the Leavitt Peak Press booth.
              </p>
            </div>

            <div className="space-y-5">
              {upcomingEvents.map((event, idx) => (
                <div
                  key={event.id}
                  className="group bg-white rounded-2xl border border-cream-200 hover:border-gold-200 overflow-hidden transition-all duration-500 hover:shadow-[0_8px_40px_rgba(196,154,58,0.1)]"
                  style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="flex flex-col lg:flex-row">
                    {/* Event Image */}
                    <div className="lg:w-72 xl:w-80 shrink-0 overflow-hidden">
                      <div className="relative h-48 lg:h-full">
                        <img
                          src={event.image}
                          alt={event.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 via-transparent to-transparent lg:bg-gradient-to-r"></div>
                        <div className="absolute top-4 left-4">
                          <span className="px-3 py-1 text-[10px] font-semibold text-white bg-gold-500 rounded-full uppercase tracking-wider whitespace-nowrap">
                            {event.status}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Event Details */}
                    <div className="flex-1 p-6 md:p-8">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4 mb-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <div className="w-9 h-9 rounded-xl bg-gold-50 flex items-center justify-center">
                              <i className={`${event.icon} text-gold-600`}></i>
                            </div>
                            <p className="text-xs font-medium text-gold-600 uppercase tracking-wider">{event.subtitle}</p>
                          </div>
                          <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-3">{event.title}</h3>
                          <div className="flex flex-wrap items-center gap-4 text-sm text-navy-600 mb-4">
                            <span className="flex items-center gap-1.5">
                              <i className="ri-calendar-line text-gold-500"></i>
                              {event.date}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <i className="ri-map-pin-line text-gold-500"></i>
                              {event.location}
                            </span>
                            <span className="flex items-center gap-1.5">
                              <i className="ri-store-2-line text-gold-500"></i>
                              {event.booth}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-3">
                        {/* Watch Video button for video events */}
                        {'videoId' in event && (event as any).videoId && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleEvent(event.id);
                            }}
                            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-red-600 rounded-full hover:bg-red-700 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                          >
                            <i className="ri-youtube-fill text-lg"></i>
                            Watch on YouTube
                          </button>
                        )}
                        <button
                          onClick={() => toggleEvent(event.id)}
                          className="flex items-center gap-2 text-sm font-medium text-navy-500 hover:text-gold-600 transition-colors duration-300"
                        >
                          {expandedEvent === event.id ? 'Show Less' : 'View Details'}
                          <i className={`ri-arrow-down-s-line transition-transform duration-300 ${expandedEvent === event.id ? 'rotate-180' : ''}`}></i>
                        </button>
                      </div>

                      {/* Expandable Details */}
                      <div
                        className={`overflow-hidden transition-all duration-500 ${
                          expandedEvent === event.id ? 'max-h-[800px] opacity-100 mt-4' : 'max-h-0 opacity-0'
                        }`}
                      >
                        <div className="border-t border-cream-200 pt-5">
                          {/* Video embed for video events */}
                          {'videoId' in event && (event as any).videoId && (
                            <div className="relative w-full aspect-video mb-5 rounded-xl overflow-hidden">
                              <iframe
                                className="w-full h-full"
                                src={`https://www.youtube.com/embed/${(event as any).videoId}`}
                                title={event.title}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                              ></iframe>
                            </div>
                          )}
                          <p className="text-sm text-navy-700 leading-relaxed mb-5">{event.description}</p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-5">
                            {event.highlights.map((item) => (
                              <div key={item} className="flex items-center gap-2 text-sm text-navy-600">
                                <div className="w-6 h-6 rounded-full bg-gold-100 flex items-center justify-center shrink-0">
                                  <i className="ri-check-line text-gold-600 text-xs"></i>
                                </div>
                                {item}
                              </div>
                            ))}
                          </div>
                          {'videoId' in event && (event as any).videoId ? (
                            <a
                              href={`https://youtu.be/${(event as any).videoId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-red-600 rounded-full hover:bg-red-700 transition-all duration-300 hover:scale-105"
                            >
                              <i className="ri-youtube-fill"></i>
                              Watch Full Video
                            </a>
                          ) : (
                            <a
                              href="/contact-us"
                              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105"
                            >
                              Reserve Your Spot
                              <i className="ri-arrow-right-line"></i>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ PAST EVENTS ============ */}
      <section className="w-full py-14 md:py-20 bg-white">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={pastAnim.ref} className={`max-w-6xl mx-auto animate-on-scroll ${pastAnim.isVisible ? 'animate-visible' : ''}`}>
            <div className="text-center mb-10">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Looking Back</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Past Events & Highlights</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
            </div>

            {/* Verification strip */}
            <div className="flex flex-col sm:flex-row items-center gap-4 bg-cream-50 border border-cream-200 rounded-2xl px-6 py-5 mb-8">
              <span className="w-10 h-10 flex items-center justify-center rounded-xl bg-gold-100 text-gold-600 shrink-0">
                <i className="ri-shield-check-line text-lg"></i>
              </span>
              <p className="text-sm text-navy-700 leading-relaxed text-center sm:text-left flex-1">
                Every appearance on this page is documented with dates, venue details, and photographs
                taken on the floor.
              </p>
              <Link
                to="/events/verified-participation"
                className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-colors duration-300 whitespace-nowrap"
              >
                Verified Participation
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>

            {/* Past Events Tabs */}
            <div className="flex flex-wrap items-center gap-2 mb-8">
              {pastEvents.map((event, idx) => (
                <button
                  key={event.id}
                  onClick={() => setActivePastEvent(idx)}
                  className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                    activePastEvent === idx
                      ? 'bg-navy-500 text-white shadow-[0_4px_15px_rgba(8,30,54,0.15)]'
                      : 'bg-cream-100 text-navy-600 hover:bg-cream-200'
                  }`}
                >
                  {event.title}
                </button>
              ))}
            </div>

            {/* Active Past Event */}
            {pastEvents[activePastEvent] && (
              <div className="animate-fade-in-up">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Gallery Grid */}
                  <div>
                    {pastEvents[activePastEvent].gallery.length === 0 && (
                      <div className="flex flex-col items-center justify-center aspect-[16/10] rounded-2xl bg-cream-50 border border-dashed border-cream-300 text-center px-6">
                        <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-white text-gold-600 mb-4">
                          <i className="ri-image-2-line text-2xl"></i>
                        </div>
                        <p className="text-sm font-semibold text-navy-900 mb-1.5">No photographs on file</p>
                        <p className="text-xs text-navy-500 leading-relaxed max-w-xs">
                          We only publish photographs we can date to the fair itself. Ask us and we will send what
                          we hold for this edition.
                        </p>
                      </div>
                    )}
                    {pastEvents[activePastEvent].gallery.length > 0 && (
                      <>
                    {/* Featured hero image */}
                    <div className="overflow-hidden rounded-2xl mb-3 aspect-[16/9] group cursor-pointer" onClick={() => setPastLightboxIndex(0)}>
                      <div className="relative w-full h-full">
                        <img
                          src={pastEvents[activePastEvent].gallery[0]}
                          alt={`${pastEvents[activePastEvent].title} featured photo`}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                          <div className="flex items-center gap-2">
                            <div className="w-9 h-9 rounded-full bg-gold-500/90 flex items-center justify-center">
                              <i className="ri-zoom-in-line text-white"></i>
                            </div>
                            <span className="text-sm font-medium text-white">View full size</span>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* Uniform thumbnails — equal tiles, no ragged gaps */}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 md:gap-3">
                      {pastEvents[activePastEvent].gallery.slice(1, 7).map((img, idx) => (
                        <button
                          type="button"
                          key={`${img}-${idx}`}
                          onClick={() => setPastLightboxIndex(idx + 1)}
                          className="group relative block aspect-[4/3] overflow-hidden rounded-xl cursor-pointer"
                          aria-label={`Open photograph ${idx + 2} from ${pastEvents[activePastEvent].title}`}
                        >
                          <img
                            src={img}
                            alt={`${pastEvents[activePastEvent].title} photo ${idx + 2}`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-navy-900/0 group-hover:bg-navy-900/30 transition-colors duration-300 flex items-center justify-center">
                            <i className="ri-zoom-in-line text-white text-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></i>
                          </div>
                        </button>
                      ))}
                    </div>
                    {/* View all link */}
                    {pastEvents[activePastEvent].gallery.length > 7 && (
                      <div className="mt-5 text-center">
                        <button
                          onClick={() => {
                            const matchTitle = pastEvents[activePastEvent].title;
                            const archivedMatch = archivedEvents.find(e => e.title === matchTitle);
                            setArchiveFilter(archivedMatch ? archivedMatch.id : 'all');
                            if (archivedMatch) {
                              setExpandedGroups((current) => ({ ...current, [archivedMatch.id]: true }));
                            }
                            document.getElementById('archived-gallery')?.scrollIntoView({ behavior: 'smooth' });
                          }}
                          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium text-navy-600 bg-cream-100 hover:bg-cream-200 rounded-full transition-all duration-300 whitespace-nowrap"
                        >
                          <i className="ri-gallery-line"></i>
                          View All {pastEvents[activePastEvent].gallery.length} Photos
                          <i className="ri-arrow-down-line"></i>
                        </button>
                      </div>
                    )}
                      </>
                    )}
                  </div>

                  {/* Event Info */}
                  <div>
                    <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold-50 border border-gold-200 rounded-full mb-4">
                      <i className="ri-check-double-line text-gold-600 text-xs"></i>
                      <span className="text-xs font-medium text-gold-700">Completed</span>
                    </div>
                    <h3 className="text-xl md:text-2xl font-bold text-navy-900 mb-2">
                      {pastEvents[activePastEvent].title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-navy-600 mb-4">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-calendar-line text-gold-500"></i>
                        {pastEvents[activePastEvent].date}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-map-pin-line text-gold-500"></i>
                        {pastEvents[activePastEvent].location}
                      </span>
                    </div>
                    <p className="text-sm text-navy-700 leading-relaxed mb-6">
                      {pastEvents[activePastEvent].description}
                    </p>

                    {/* Stats Grid */}
                    <div className="grid grid-cols-2 gap-4 mb-6">
                      {Object.entries(pastEvents[activePastEvent].stats).map(([key, value]) => (
                        <div key={key} className="bg-cream-50 rounded-xl p-4 border border-cream-200">
                          <p className="text-2xl font-bold text-navy-900 mb-1">{value}</p>
                          <p className="text-xs text-navy-500 capitalize">{key}</p>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                      {pastEvents[activePastEvent].id === 108 && (
                        <Link
                          to="/events/frankfurt-book-fair-2026"
                          className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap"
                        >
                          Read the Full Event Report
                          <i className="ri-article-line"></i>
                        </Link>
                      )}
                      <a
                        href="/contact-us"
                        className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-navy-500 rounded-full hover:bg-navy-600 transition-all duration-300 hover:scale-105"
                      >
                        Join Our Next Event
                        <i className="ri-arrow-right-line"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ ARCHIVED EVENTS GALLERY ============ */}
      <section id="archived-gallery" className="w-full py-14 md:py-20 bg-gradient-to-br from-cream-50 via-white to-cream-100">
        <div className="w-full px-4 md:px-6 lg:px-8">
          <div ref={archiveAnim.ref} className="max-w-6xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-xs font-medium text-gold-600 uppercase tracking-[0.2em] mb-3">Photo Archives</p>
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-navy-900">Archived Events Gallery</h2>
              <div className="w-16 h-1 bg-gold-400 rounded-full mx-auto mt-4"></div>
              <p className="text-sm text-navy-600 mt-4 max-w-2xl mx-auto">
                Browse through our complete photo archives from every book fair, signing, and publishing event we've attended. Click any photo to view it full-size.
              </p>
            </div>

            {/* ============ FACEBOOK PAGE LINK ============ */}
            <div className="mb-10">
              <div className="bg-white rounded-2xl border border-cream-200 py-8 px-6 flex flex-col items-center justify-center gap-4">
                <div className="w-14 h-14 rounded-full bg-[#1877F2]/10 flex items-center justify-center">
                  <i className="ri-facebook-fill text-[#1877F2] text-2xl"></i>
                </div>
                <div className="text-center">
                  <h3 className="text-base font-bold text-navy-900 mb-1">Live from Leavitt Peak Press</h3>
                  <p className="text-xs text-navy-500">Follow our latest updates on Facebook</p>
                </div>
                <a
                  href="https://www.facebook.com/LeavittPeakPress"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-2.5 text-sm font-semibold text-white bg-[#1877F2] hover:bg-[#166FE5] rounded-full transition-all duration-300 hover:scale-105 whitespace-nowrap"
                >
                  <i className="ri-facebook-fill"></i>
                  View Page
                </a>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex flex-wrap items-center justify-center gap-2 mb-10">
              <button
                onClick={() => setArchiveFilter('all')}
                className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                  archiveFilter === 'all'
                    ? 'bg-navy-500 text-white shadow-[0_4px_15px_rgba(8,30,54,0.15)]'
                    : 'bg-cream-100 text-navy-600 hover:bg-cream-200'
                }`}
              >
                All Photos
                <span className="ml-1.5 text-xs opacity-70">({totalArchivedPhotos})</span>
              </button>
              {archivedEvents.map((evt) => (
                <button
                  key={evt.id}
                  onClick={() => setArchiveFilter(evt.id)}
                  className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap ${
                    archiveFilter === evt.id
                      ? 'bg-navy-500 text-white shadow-[0_4px_15px_rgba(8,30,54,0.15)]'
                      : 'bg-cream-100 text-navy-600 hover:bg-cream-200'
                  }`}
                >
                  {evt.title}
                  <span className="ml-1.5 text-xs opacity-70">({evt.photos.length})</span>
                </button>
              ))}
            </div>

            {/* Active Filter Event Info */}
            {archiveFilter !== 'all' && (() => {
              const activeEvent = archivedEvents.find(e => e.id === archiveFilter);
              if (!activeEvent) return null;
              return (
                <div className="bg-white rounded-2xl border border-cream-200 p-6 mb-8 flex flex-col sm:flex-row items-start gap-5 animate-fade-in-up">
                  <div className="w-12 h-12 rounded-2xl bg-gold-100 flex items-center justify-center shrink-0">
                    <i className="ri-image-2-line text-gold-600 text-xl"></i>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-navy-900 mb-1">{activeEvent.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm text-navy-600 mb-2">
                      <span className="flex items-center gap-1.5">
                        <i className="ri-calendar-line text-gold-500"></i>
                        {activeEvent.date}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <i className="ri-map-pin-line text-gold-500"></i>
                        {activeEvent.location}
                      </span>
                    </div>
                    <p className="text-sm text-navy-700 leading-relaxed">{activeEvent.description}</p>
                  </div>
                  <div className="flex items-center gap-1.5 px-4 py-2 bg-gold-50 rounded-full text-sm font-semibold text-gold-700 shrink-0">
                    <i className="ri-camera-line"></i>
                    <span>{activeEvent.photos.length} Photos</span>
                  </div>
                </div>
              );
            })()}

            {/* Photo grid — grouped into event albums, uniform tiles */}
            {archiveGroupsWithOffset.length > 0 ? (
              <div className="space-y-10 md:space-y-12">
                {archiveGroupsWithOffset.map((group) => (
                  <div key={group.id}>
                    {/* Album header — hidden when a single event is filtered, the info card already names it */}
                    {archiveFilter === 'all' && (
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4 pb-3 border-b border-cream-200">
                      <div className="min-w-0">
                        <h3 className="text-base md:text-lg font-bold text-navy-900">{group.title}</h3>
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-navy-500 mt-1">
                          <span className="flex items-center gap-1.5 whitespace-nowrap">
                            <i className="ri-calendar-line text-gold-500"></i>
                            {group.date}
                          </span>
                          <span className="flex items-center gap-1.5">
                            <i className="ri-map-pin-line text-gold-500"></i>
                            {group.location}
                          </span>
                        </div>
                      </div>
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-white border border-cream-200 rounded-full text-xs font-medium text-navy-600 shrink-0 self-start sm:self-auto">
                        <i className="ri-camera-line text-gold-500"></i>
                        {group.photos.length} photos
                      </span>
                    </div>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
                      {group.visiblePhotos.map((src, idx) => (
                        <button
                          key={`${group.id}-${idx}`}
                          type="button"
                          onClick={() => setArchiveLightboxIndex(group.offset + idx)}
                          className="group relative block aspect-[4/3] overflow-hidden rounded-xl border border-cream-200 cursor-pointer"
                          aria-label={`Open photograph ${idx + 1} from ${group.title}`}
                        >
                          <img
                            src={src}
                            alt={`${group.title} — photograph ${idx + 1}`}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-navy-950/80 via-navy-950/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-3 md:p-4">
                            <span className="text-[10px] font-medium text-gold-300 uppercase tracking-wider mb-1 truncate">
                              {group.title}
                            </span>
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-semibold text-white">
                                {String(idx + 1).padStart(2, '0')} / {String(group.visiblePhotos.length).padStart(2, '0')}
                              </span>
                              <span className="w-7 h-7 shrink-0 rounded-full bg-gold-500/90 flex items-center justify-center">
                                <i className="ri-zoom-in-line text-white text-xs"></i>
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>

                    {group.photos.length > ARCHIVE_PREVIEW && (
                      <div className="mt-4 text-center">
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedGroups((current) => ({ ...current, [group.id]: !current[group.id] }))
                          }
                          className="inline-flex items-center gap-2 px-5 py-2.5 text-xs font-semibold text-navy-600 bg-white border border-cream-200 rounded-full hover:border-gold-300 hover:text-gold-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
                        >
                          <i className={expandedGroups[group.id] ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'}></i>
                          {expandedGroups[group.id]
                            ? 'Show fewer photos'
                            : `Show all ${group.photos.length} photos`}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center rounded-full bg-cream-100 text-cream-300">
                  <i className="ri-image-line text-3xl"></i>
                </div>
                <p className="text-navy-500 text-sm">No photos found for this filter.</p>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* ============ CTA SECTION ============ */}
      <section className="w-full py-16 md:py-20 bg-gradient-to-r from-navy-900 via-navy-800 to-navy-900 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-64 h-64 bg-gold-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-80 h-80 bg-navy-400/10 rounded-full blur-3xl"></div>
        </div>
        <div className="absolute inset-0 animate-shimmer pointer-events-none"></div>

        <div ref={ctaAnim.ref} className={`w-full px-4 md:px-6 lg:px-8 relative z-10 animate-on-scroll ${ctaAnim.isVisible ? 'animate-visible' : ''}`}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold-500/20 border border-gold-400/30 rounded-full mb-6">
              <i className="ri-sparkling-2-line text-gold-400 text-sm"></i>
              <span className="text-xs font-medium text-gold-300 uppercase tracking-widest">Limited Spots Available</span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Showcase Your Book at the Next <span className="text-gold-400">Major Event?</span>
            </h2>
            <p className="text-sm md:text-base text-cream-200 leading-relaxed mb-8 max-w-2xl mx-auto">
              Secure a spot for your book at our booth during upcoming international book fairs. Let us help you connect with publishers, agents, and readers from around the world. Limited booth space available — reserve early to guarantee placement.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="/contact-us"
                className="w-full sm:w-auto px-8 py-4 text-sm font-semibold text-navy-900 bg-gold-400 rounded-full hover:bg-gold-300 transition-all duration-300 hover:scale-105 whitespace-nowrap shadow-[0_4px_20px_rgba(196,154,58,0.3)]"
              >
                Reserve Your Booth
              </a>
              <a
                href="https://www.amazon.com/s?k=leavitt+peak+press"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full sm:w-auto px-8 py-4 text-sm font-medium text-white border border-white/30 rounded-full hover:bg-white/10 hover:border-white/50 transition-all duration-300 hover:scale-105 whitespace-nowrap"
              >
                Browse Our Books
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Photo viewers */}
      <PhotoViewer
        photos={pastEvents[activePastEvent]?.gallery ?? []}
        index={pastLightboxIndex}
        label={pastEvents[activePastEvent]?.title ?? 'Event photograph'}
        onClose={() => setPastLightboxIndex(null)}
        onChange={setPastLightboxIndex}
      />
      <PhotoViewer
        photos={visibleArchivePhotos}
        index={archiveLightboxIndex}
        label="Event archive photograph"
        onClose={() => setArchiveLightboxIndex(null)}
        onChange={setArchiveLightboxIndex}
      />
    </main>
  );
}